import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { FilePlus2, CheckCircle2 } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { TextField } from '@components/base/inputs';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  FieldConfig,
  SelectFieldOptions,
  DateFieldOptions,
  TimeFieldOptions,
  TextareaOptions,
  TextFieldOptions,
  UseEntityFormReturn,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  DEFAULT_REQUIRED_MESSAGE,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';

interface OuvertureSinistreCardProps {
  ses: string;
  idSin?: string;
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render). Every form field now lives in `EntityFormConfig.fields` with an
   * explicit `order`, so a `reorderFields` rule reorders them without a JSX
   * edit. The Assuré / Immatriculation typeaheads stay bespoke async widgets
   * but now carry **descriptor-only** config fields (`assure`, `immat`) so a
   * rename / hide / reorder rule reaches their label / placeholder /
   * visibility; the spliced control reads those off the override-merged config.
   * The Heure de survenance control is now a first-class `time` field
   * (`heureSurvenance`). The two grid spacers are pinned to anchor fields.
   */
  _layoutOverrides?: LayoutOverrides;
}

/**
 * Stable keys for the two bespoke async typeaheads. They are **not** stock
 * auto-rendered fields (they drive bespoke typeahead state and their own
 * inline result list), so they are excluded from the field auto-render stream
 * and spliced at fixed anchors. They DO carry a config descriptor under these
 * keys (label / placeholder / visible / order), so a rule renames / hides them
 * via `applyLayoutOverrides` and the published config surfaces them as targets.
 */
const ASSURE_FIELD_KEY = 'assure';
const IMMAT_FIELD_KEY = 'immat';

/**
 * Fields after which an empty grid spacer is spliced, preserving the historic
 * single-column rows in the two-column grid.
 */
const SPACER_AFTER_FIELDS = new Set(['dateDeclaration', 'risque4']);

interface ErpProxyResponse<T> {
  success: boolean;
  data: { total: number; data: T[] };
  error?: { code: string; message: string } | null;
}

interface PerAssRow {
  ID_PER: number | string;
  NOM2_PER: string;
}

interface PolRow {
  ID_POL: number | string;
  NUM_POL: string;
}

interface VehRow {
  ID_VEH: number | string;
  IMMAT_VEH: string;
}

interface CodeLibRow {
  CODE: string;
  LIB: string;
}

interface CdCircRow {
  CD_CIRC: string;
  LIB_CIRC: string;
}

interface CdIdaRow {
  CD_IDA: string;
  LIB_IDA: string;
}

interface Gestion2Row {
  CODE: string;
  VALEUR: string;
}

interface SinistreResult {
  id: number;
  num: string | null;
  message: string | null;
}

interface SinistreResponse {
  success: boolean;
  data: SinistreResult;
}

const ASSURE_DEBOUNCE_MS = 300;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDdMmYyyy(d: Date): string {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function extractServerMessage(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== 'object' || data === null) return undefined;
  const root = data as Record<string, unknown>;
  if (typeof root.message === 'string' && root.message.length > 0) {
    return root.message;
  }
  const inner = root.data;
  if (typeof inner === 'object' && inner !== null) {
    const innerMsg = (inner as Record<string, unknown>).message;
    if (typeof innerMsg === 'string' && innerMsg.length > 0) return innerMsg;
  }
  const error = root.error;
  if (typeof error === 'object' && error !== null) {
    const m = (error as Record<string, unknown>).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return undefined;
}

export function OuvertureSinistreCard({
  ses,
  _layoutOverrides,
}: Readonly<OuvertureSinistreCardProps>) {
  const [submitted, setSubmitted] = useState(false);
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded.
  const [expanded, setExpanded] = useState(false);

  const [assureRows, setAssureRows] = useState<PerAssRow[]>([]);
  const [polRows, setPolRows] = useState<PolRow[]>([]);
  const [vehRows, setVehRows] = useState<VehRow[]>([]);
  const [typeRows, setTypeRows] = useState<CodeLibRow[]>([]);
  const [respRows, setRespRows] = useState<CodeLibRow[]>([]);
  const [circRows, setCircRows] = useState<CdCircRow[]>([]);
  const [idaRows, setIdaRows] = useState<CdIdaRow[]>([]);
  const [gestionRows, setGestionRows] = useState<CodeLibRow[]>([]);
  const [gestion2Rows, setGestion2Rows] = useState<Gestion2Row[]>([]);

  const [submittedAssure, setSubmittedAssure] = useState('');
  const [submittedPolice, setSubmittedPolice] = useState('');
  const [submittedImmat, setSubmittedImmat] = useState('');

  const [assureQuery, setAssureQuery] = useState('');
  const [assureLabel, setAssureLabel] = useState('');
  const [assureOpen, setAssureOpen] = useState(false);
  const [assureActiveIndex, setAssureActiveIndex] = useState(0);

  const [vehQuery, setVehQuery] = useState('');
  const [vehLabel, setVehLabel] = useState('');
  const [vehOpen, setVehOpen] = useState(false);
  const [vehActiveIndex, setVehActiveIndex] = useState(0);

  const formRef = useRef<UseEntityFormReturn | null>(null);
  // Required-state for the two bespoke custom-slot typeaheads, derived from the
  // override-merged config each render and read by `mutationFn` (which is
  // defined before `formConfig`, so it cannot read the merged config directly —
  // mirrors the existing `formRef.current = form` escape hatch). Seeded with the
  // baseline required-by-default so the very first render's gate is correct.
  const requiredStateRef = useRef<{
    assure: { isRequired: boolean; message: string };
    immat: { isRequired: boolean; message: string };
  }>({
    assure: { isRequired: true, message: 'Veuillez sélectionner un assuré' },
    immat: { isRequired: true, message: 'Veuillez sélectionner une immatriculation' },
  });
  const assureReqIdRef = useRef(0);
  const polReqIdRef = useRef(0);
  const vehReqIdRef = useRef(0);
  const gestion2ReqIdRef = useRef(0);
  const assureDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const assureWrapperRef = useRef<HTMLDivElement | null>(null);
  const vehWrapperRef = useRef<HTMLDivElement | null>(null);

  // Static option fetches on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchType() {
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<CodeLibRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-typsin',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && r.data.success) setTypeRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    }
    async function fetchResp() {
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<CodeLibRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-txrespsin',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && r.data.success) setRespRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    }
    async function fetchCirc() {
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<CdCircRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-cdcirc',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && r.data.success) setCircRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    }
    async function fetchIda() {
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<CdIdaRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-cdida',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && r.data.success) setIdaRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    }
    async function fetchGestion() {
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<CodeLibRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-gestion',
          { params: { SES: ses, perPage: 9999 } },
        );
        if (!cancelled && r.data.success) setGestionRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    }

    fetchType();
    fetchResp();
    fetchCirc();
    fetchIda();
    fetchGestion();

    return () => {
      cancelled = true;
    };
  }, [ses]);

  const fetchAssureNow = useCallback(
    async (q: string) => {
      const reqId = ++assureReqIdRef.current;
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<PerAssRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-idperass',
          { params: { SES: ses, q, perPage: 50 } },
        );
        if (reqId !== assureReqIdRef.current) return;
        if (r.data.success) setAssureRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    },
    [ses],
  );

  const scheduleAssureFetch = useCallback(
    (q: string) => {
      if (assureDebounceRef.current) clearTimeout(assureDebounceRef.current);
      assureDebounceRef.current = setTimeout(() => {
        void fetchAssureNow(q);
      }, ASSURE_DEBOUNCE_MS);
    },
    [fetchAssureNow],
  );

  useEffect(() => {
    return () => {
      if (assureDebounceRef.current) clearTimeout(assureDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!assureOpen && !vehOpen) return;
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (assureOpen && assureWrapperRef.current && !assureWrapperRef.current.contains(target)) {
        setAssureOpen(false);
      }
      if (vehOpen && vehWrapperRef.current && !vehWrapperRef.current.contains(target)) {
        setVehOpen(false);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [assureOpen, vehOpen]);

  useEffect(() => {
    setAssureActiveIndex(0);
  }, [assureRows]);

  useEffect(() => {
    setVehActiveIndex(0);
  }, [vehRows, vehQuery]);

  const fetchPolicies = useCallback(
    async (idPerAss: string) => {
      const reqId = ++polReqIdRef.current;
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<PolRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-idpol',
          { params: { SES: ses, id_per_ass: idPerAss, perPage: 9999 } },
        );
        if (reqId !== polReqIdRef.current) return;
        if (r.data.success) setPolRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    },
    [ses],
  );

  const fetchVehicles = useCallback(
    async (idPol: string) => {
      const reqId = ++vehReqIdRef.current;
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<VehRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-idveh',
          { params: { SES: ses, id_pol: idPol, perPage: 9999 } },
        );
        if (reqId !== vehReqIdRef.current) return;
        if (r.data.success) setVehRows(r.data.data.data);
      } catch {
        /* swallow */
      }
    },
    [ses],
  );

  const fetchGestion2 = useCallback(
    async (gestion: string) => {
      const reqId = ++gestion2ReqIdRef.current;
      try {
        const r = await claimflowApiClient.get<ErpProxyResponse<Gestion2Row>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-gestion2',
          { params: { SES: ses, gestion, perPage: 9999 } },
        );
        if (reqId !== gestion2ReqIdRef.current) return;
        if (r.data.success) setGestion2Rows(r.data.data.data);
      } catch {
        /* swallow */
      }
    },
    [ses],
  );

  const polItems = useMemo(
    () => polRows.map((r) => ({ value: String(r.ID_POL), label: r.NUM_POL })),
    [polRows],
  );
  const typeItems = useMemo(
    () => typeRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [typeRows],
  );
  const respItems = useMemo(
    () => respRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [respRows],
  );
  const circItems = useMemo(
    () => circRows.map((r) => ({ value: r.CD_CIRC, label: r.LIB_CIRC })),
    [circRows],
  );
  const idaItems = useMemo(
    () => idaRows.map((r) => ({ value: r.CD_IDA, label: r.LIB_IDA })),
    [idaRows],
  );
  const gestionItems = useMemo(
    () => gestionRows.map((r) => ({ value: r.CODE, label: r.LIB })),
    [gestionRows],
  );
  const gestion2Items = useMemo(
    () =>
      gestion2Rows.map((r) => ({ value: r.CODE, label: r.VALEUR })),
    [gestion2Rows],
  );

  const today = useMemo(() => startOfDay(new Date()), []);

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const data =
        payload instanceof FormData
          ? Object.fromEntries(payload.entries())
          : payload;

      const idPerAssRaw = String(
        (data as Record<string, unknown>).idPerAss ??
          formRef.current?.getValue('idPerAss') ??
          '',
      );
      // Config-driven submit gate: only enforce when the override-merged
      // `assure` descriptor marks the field required, and throw its (overridable)
      // message. Default is required-by-default ⇒ identical behavior/message.
      const assureReq = requiredStateRef.current.assure;
      if (assureReq.isRequired && !idPerAssRaw) {
        throw new Error(assureReq.message);
      }

      const survenanceDate = data.dateSurvenance instanceof Date
        ? data.dateSurvenance
        : null;
      const declarationDate = data.dateDeclaration instanceof Date
        ? data.dateDeclaration
        : null;

      if (survenanceDate && startOfDay(survenanceDate) > today) {
        throw new Error('La date de survenance ne peut pas être dans le futur.');
      }
      if (
        survenanceDate &&
        declarationDate &&
        startOfDay(declarationDate) < startOfDay(survenanceDate)
      ) {
        throw new Error(
          'La date de déclaration doit être postérieure à la date de survenance.',
        );
      }

      const dateSurvenance = survenanceDate ? formatDdMmYyyy(survenanceDate) : '';
      const dateDeclaration = declarationDate ? formatDdMmYyyy(declarationDate) : '';
      const dateOuverture = formatDdMmYyyy(today);

      const idVehSelected = String(
        (data as Record<string, unknown>).idVeh ??
          formRef.current?.getValue('idVeh') ??
          '',
      );
      // Config-driven submit gate for the Immatriculation custom slot (same
      // pattern as `assure` above).
      const immatReq = requiredStateRef.current.immat;
      if (immatReq.isRequired && !idVehSelected) {
        throw new Error(immatReq.message);
      }
      // The hour now lives in the EntityForm config (`heureSurvenance`, a
      // `time` field holding a `HH:mm` string). Read it off the submitted form
      // values (with a `formRef` fallback), so the outgoing payload stays
      // byte-identical to the old hand-placed TimePicker path (this card writes
      // the raw `HH:mm`).
      const heureRaw = data.heureSurvenance ?? formRef.current?.getValue('heureSurvenance');
      const heureSurvenance = typeof heureRaw === 'string' ? heureRaw : '';
      if (!heureSurvenance) {
        throw new Error('Veuillez saisir une heure de survenance');
      }
      const veh = vehRows.find((v) => String(v.ID_VEH) === idVehSelected);

      const infos: Record<string, string> = {
        NATSIN: '+RC PRO',
        BRANCHE: '1',
      };
      const setInfo = (key: string, value: unknown) => {
        const s = String(value ?? '');
        if (s) infos[key] = s;
      };
      setInfo('GESTION', data.gestion);
      setInfo('GESTION2', data.gestion2);
      setInfo('DESC', data.desc);
      setInfo('VAL_INFS', data.observations);
      setInfo('REFCLIENT', data.refClient);

      const body: Record<string, unknown> = {
        idPol: String(data.idPol ?? ''),
        numSoc: '1',
        mouvement: 'OUVSIN',
        motif: 'OUVSIN',
        loadAssure: '1',
        dateOuverture,
        dateSurvenance,
        infos,
      };
      const set = (key: string, value: unknown) => {
        const s = String(value ?? '');
        if (s) body[key] = s;
      };
      set('numCie', data.numCie);
      set('numCbt', data.numCbt);
      set('idElem', idVehSelected);
      set('refRisque', veh?.IMMAT_VEH);
      set('type', data.type);
      set('txResp', data.txResp);
      set('circonstance', data.circonstance);
      set('causeCirconstance', data.causeCirconstance);
      set('heureSurvenance', heureSurvenance);
      set('dateDeclaration', dateDeclaration);
      set('dommages', data.dommages);
      set('risque4', data.risque4);
      set('cdIda', data.cdIda);

      try {
        const response = await claimflowApiClient.post<SinistreResponse>(
          '/claimflow/erp/registries/veos/endpoints/sinistre',
          { data: body },
        );
        const result = response.data?.data;
        if (
          result &&
          result.message === null &&
          typeof result.id === 'number' &&
          result.id > 0
        ) {
          return result;
        }
        const message =
          (result && typeof result.message === 'string' && result.message) ||
          'Échec de l’ouverture du sinistre.';
        throw new Error(message);
      } catch (err) {
        const serverMessage = extractServerMessage(err);
        if (serverMessage) throw new Error(serverMessage);
        throw err;
      }
    },
    [today, vehRows],
  );

  const baseConfig: EntityFormConfig = useMemo(
    () => ({
          id: 'ouverture-sinistre',
          endpoint: {
            erpEndpoint: '/claimflow/erp/registries/veos/endpoints/sinistre',
            method: 'POST',
          },
          // Every field carries an explicit `order` reproducing the historic
          // hand-placed grid (screenshot order). The render below maps the
          // config sorted by `order`, so a `reorderFields` rule reorders the
          // fields with no JSX edit. The Assuré (`assure`) and Immatriculation
          // (`immat`) typeaheads stay bespoke async widgets but each carries a
          // **descriptor-only** field here (label / placeholder / visible /
          // order) so a rename / hide rule reaches them — they are excluded
          // from the stock auto-render and the bespoke control is spliced at
          // the descriptor's sorted position. The two empty grid spacers are
          // pinned to anchor fields likewise.
          fields: {
            // Descriptor-only: the Assuré async typeahead reads its label /
            // placeholder / visibility off this merged descriptor and renders
            // as the first grid cell (order 1). Not stock auto-rendered.
            [ASSURE_FIELD_KEY]: {
              type: 'text',
              label: 'Assuré',
              order: 1,
              colSpan: 2,
              // Structurally required by default — the card cannot function
              // without an assuré (the police/vehicle cascades and the outgoing
              // payload depend on it). Carried here as config (not a hardcoded
              // JSX `required`) so the asterisk + the submit gate are
              // config-driven and an admin add-only `requireField` rule is a
              // no-op (baseRequired already true). The message reproduces the
              // historic `mutationFn` throw verbatim for exact parity.
              validation: { required: 'Veuillez sélectionner un assuré' },
              placeholder: 'Rechercher un assuré...',
              options: {} as TextFieldOptions,
            },
            idPol: {
              type: 'select',
              label: 'Police',
              order: 2,
              colSpan: 2,
              validation: { required: 'Veuillez sélectionner une police' },
              options: {
                items: polItems,
                searchable: true,
                placeholder: 'Sélectionner d’abord un assuré',
              } as SelectFieldOptions,
            },
            // Descriptor-only: the Immatriculation async typeahead reads its
            // label / placeholder / visibility off this merged descriptor and
            // renders right after `idPol` (order 3). Not stock auto-rendered.
            [IMMAT_FIELD_KEY]: {
              type: 'text',
              label: 'Immatriculation',
              order: 3,
              colSpan: 2,
              // Structurally required by default — the outgoing payload's
              // `idElem` / `refRisque` derive from the selected vehicle. Config
              // (not a hardcoded JSX `required`) so the asterisk + submit gate
              // are config-driven; message reproduces the historic `mutationFn`
              // throw verbatim. baseRequired true ⇒ add-only rule is a no-op.
              validation: { required: 'Veuillez sélectionner une immatriculation' },
              placeholder: 'Rechercher une immatriculation...',
              options: {} as TextFieldOptions,
            },
            dateSurvenance: {
              type: 'date',
              label: 'Date de survenance',
              order: 4,
              validation: { required: 'Date requise' },
              placeholder: 'jj/mm/aaaa',
              options: {
                displayFormat: 'dd/MM/yyyy',
                maxDate: today.toISOString(),
              } as DateFieldOptions,
            },
            // Folded the former hand-placed `<TimePicker label="Heure de
            // survenance">` into the declarative config so its label /
            // placeholder / visibility / order are tenant-layout-configurable
            // (rename / hide / reorder via the rule engine) like every other
            // field. The `time` type maps to the same base TimePicker; its
            // value is a `HH:mm` string written verbatim to the flux payload
            // (this card stores the raw `HH:mm`, not a 2-digit hour). Holds the
            // historic order-5 slot (right after `dateSurvenance`).
            heureSurvenance: {
              type: 'time',
              label: 'Heure de survenance',
              order: 5,
              validation: { required: 'Veuillez saisir une heure de survenance' },
              placeholder: '--:--',
              options: {} as TimeFieldOptions,
            },
            dateDeclaration: {
              type: 'date',
              label: 'Date de déclaration',
              order: 6,
              placeholder: 'jj/mm/aaaa',
              options: {
                displayFormat: 'dd/MM/yyyy',
              } as DateFieldOptions,
            },
            numCie: {
              type: 'text',
              label: 'Réf Cie',
              order: 7,
              validation: { required: 'Réf Cie requise' },
              options: {} as TextFieldOptions,
            },
            numCbt: {
              type: 'text',
              label: 'Réf Cbt',
              order: 8,
              validation: { required: 'Réf Cbt requise' },
              options: {} as TextFieldOptions,
            },
            refClient: {
              type: 'text',
              label: 'Réf client',
              order: 9,
              colSpan: 2,
              options: {} as TextFieldOptions,
            },
            type: {
              type: 'select',
              label: 'Type',
              order: 10,
              colSpan: 2,
              validation: { required: 'Type requis' },
              options: {
                items: typeItems,
                searchable: true,
                placeholder: 'Sélectionner un type',
              } as SelectFieldOptions,
            },
            txResp: {
              type: 'select',
              label: 'Responsabilité',
              order: 11,
              colSpan: 2,
              validation: { required: 'Responsabilité requise' },
              options: {
                items: respItems,
                searchable: true,
                placeholder: 'Sélectionner une responsabilité',
              } as SelectFieldOptions,
            },
            circonstance: {
              type: 'select',
              label: 'Circonstance',
              order: 12,
              colSpan: 2,
              validation: { required: 'Circonstance requise' },
              options: {
                items: circItems,
                searchable: true,
                placeholder: 'Sélectionner une circonstance',
              } as SelectFieldOptions,
            },
            causeCirconstance: {
              type: 'textarea',
              label: 'Cause circonstances',
              order: 13,
              colSpan: 2,
              options: { rows: 3 } as TextareaOptions,
            },
            desc: {
              type: 'textarea',
              label: 'Description',
              order: 14,
              colSpan: 2,
              options: { rows: 3 } as TextareaOptions,
            },
            dommages: {
              type: 'textarea',
              label: 'Dommages',
              order: 15,
              colSpan: 2,
              options: { rows: 3 } as TextareaOptions,
            },
            risque4: {
              type: 'text',
              label: 'Code postal',
              order: 16,
              validation: { required: 'Code postal requis' },
              options: { maxLength: 10 } as TextFieldOptions,
            },
            gestion: {
              type: 'select',
              label: 'Gestion',
              order: 17,
              options: {
                items: gestionItems,
                searchable: true,
                placeholder: 'Sélectionner une gestion',
              } as SelectFieldOptions,
            },
            gestion2: {
              type: 'select',
              label: 'Gestion 2',
              order: 18,
              options: {
                items: gestion2Items,
                searchable: true,
                placeholder: 'Sélectionner d’abord une gestion',
              } as SelectFieldOptions,
            },
            cdIda: {
              type: 'select',
              label: 'Cas',
              order: 19,
              colSpan: 2,
              validation: { required: 'Cas requis' },
              options: {
                items: idaItems,
                searchable: true,
                placeholder: 'Sélectionner un cas',
              } as SelectFieldOptions,
            },
            observations: {
              type: 'textarea',
              label: 'Observations',
              order: 20,
              colSpan: 2,
              options: { rows: 3 } as TextareaOptions,
            },
          },
          actions: {
            submit: { visible: true, label: 'Ouvrir le sinistre' },
            cancel: { visible: false },
            reset: { visible: false },
          },
          layout: { columns: 2, gap: 'sm' },
        }),
    [
      today,
      polItems,
      typeItems,
      respItems,
      circItems,
      idaItems,
      gestionItems,
      gestion2Items,
    ],
  );

  usePublishLayoutConfig(
    'claimflow.ouvertureSinistreCard',
    buildPublishedConfigFromEntityForm(baseConfig, 'Ouverture sinistre'),
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  // Config fed to `useEntityForm` (the form's Zod validation + submission). The
  // two custom-slot descriptors (`assure` / `immat`) carry `validation.required`
  // so they surface as `baseRequired: true` to the published config / admin and
  // drive the asterisk + the bespoke submit gate (below) — but their config
  // keys are NOT the form's real value keys (the widgets write `idPerAss` /
  // `idVeh`). Leaving `validation.required` on them would make EntityForm's
  // schema demand non-empty `values.assure` / `values.immat`, which never
  // exist, so the form could never submit. Strip the custom-slot `validation`
  // from the form's view of the config only (rendering + descriptor reads keep
  // the full `formConfig`); the required contract is enforced by `mutationFn`
  // via `requiredStateRef`. Identity-preserving when no stripping is needed.
  const formConfigForForm: EntityFormConfig = useMemo(() => {
    const fields = formConfig.fields;
    const assureField = fields[ASSURE_FIELD_KEY];
    const immatField = fields[IMMAT_FIELD_KEY];
    const assureHasValidation = assureField?.validation !== undefined;
    const immatHasValidation = immatField?.validation !== undefined;
    if (!assureHasValidation && !immatHasValidation) return formConfig;

    const nextFields: Record<string, FieldConfig> = { ...fields };
    if (assureField && assureHasValidation) {
      const { validation: _omit, ...rest } = assureField;
      nextFields[ASSURE_FIELD_KEY] = rest;
    }
    if (immatField && immatHasValidation) {
      const { validation: _omit, ...rest } = immatField;
      nextFields[IMMAT_FIELD_KEY] = rest;
    }
    return { ...formConfig, fields: nextFields };
  }, [formConfig]);

  // Override-merged descriptors for the two bespoke async typeaheads. The
  // spliced controls read their label / placeholder / visibility from these
  // (never hardcoded JSX strings), so a rename / hide / placeholder rule
  // reaches them. `visible === false` ⇒ the control is not rendered.
  const assureDescriptor: FieldConfig | undefined =
    formConfig.fields[ASSURE_FIELD_KEY];
  const immatDescriptor: FieldConfig | undefined =
    formConfig.fields[IMMAT_FIELD_KEY];
  const assureVisible = assureDescriptor?.visible !== false;
  const immatVisible = immatDescriptor?.visible !== false;

  // Derive required-state for the two custom-slot typeaheads from the
  // override-merged config (the stable keys `assure` / `immat`). The layout
  // merge writes `requireField` onto `validation.required` as a string (custom
  // message) or `true` (default message); a base descriptor that declares its
  // own `required` string is reported as `baseRequired: true`. This drives the
  // asterisk (the `<TextField required>` prop) and the submit gate below — no
  // hardcoded `required` literal anywhere. Default: required-by-default ⇒ exact
  // historic behavior, now config-sourced.
  const deriveRequired = (descriptor: FieldConfig | undefined) => {
    const req = descriptor?.validation?.required;
    const isRequired = req === true || typeof req === 'string';
    const message = typeof req === 'string' ? req : DEFAULT_REQUIRED_MESSAGE;
    return { isRequired, message };
  };
  const { isRequired: assureRequired, message: assureRequiredMessage } =
    deriveRequired(assureDescriptor);
  const { isRequired: immatRequired, message: immatRequiredMessage } =
    deriveRequired(immatDescriptor);

  // Expose the derived required-state to `mutationFn` (defined before the merged
  // config) via a ref, mirroring the `formRef.current = form` pattern.
  requiredStateRef.current = {
    assure: { isRequired: assureRequired, message: assureRequiredMessage },
    immat: { isRequired: immatRequired, message: immatRequiredMessage },
  };

  const filteredVehRows = useMemo(() => {
    const q = vehQuery.trim().toLowerCase();
    if (!q) return vehRows;
    return vehRows.filter((r) => r.IMMAT_VEH.toLowerCase().includes(q));
  }, [vehRows, vehQuery]);

  const pickAssure = useCallback(
    (row: PerAssRow) => {
      const id = String(row.ID_PER);
      formRef.current?.setValue('idPerAss', id);
      setAssureLabel(row.NOM2_PER);
      setAssureQuery('');
      setAssureOpen(false);
    },
    [],
  );

  const clearAssureSelection = useCallback(() => {
    formRef.current?.setValue('idPerAss', '');
    setAssureLabel('');
  }, []);

  const pickVeh = useCallback((row: VehRow) => {
    const id = String(row.ID_VEH);
    formRef.current?.setValue('idVeh', id);
    setVehLabel(row.IMMAT_VEH);
    setVehQuery('');
    setVehOpen(false);
  }, []);

  const clearVehSelection = useCallback(() => {
    formRef.current?.setValue('idVeh', '');
    setVehLabel('');
  }, []);

  const handleAssureKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!assureOpen || assureRows.length === 0) {
        if (e.key === 'Escape') setAssureOpen(false);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setAssureActiveIndex((i) => (i + 1) % assureRows.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setAssureActiveIndex((i) => (i - 1 + assureRows.length) % assureRows.length);
      } else if (e.key === 'Enter') {
        const candidate = assureRows[assureActiveIndex];
        if (candidate) {
          e.preventDefault();
          pickAssure(candidate);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setAssureOpen(false);
      }
    },
    [assureOpen, assureRows, assureActiveIndex, pickAssure],
  );

  const handleVehKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!vehOpen || filteredVehRows.length === 0) {
        if (e.key === 'Escape') setVehOpen(false);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setVehActiveIndex((i) => (i + 1) % filteredVehRows.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setVehActiveIndex(
          (i) => (i - 1 + filteredVehRows.length) % filteredVehRows.length,
        );
      } else if (e.key === 'Enter') {
        const candidate = filteredVehRows[vehActiveIndex];
        if (candidate) {
          e.preventDefault();
          pickVeh(candidate);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setVehOpen(false);
      }
    },
    [vehOpen, filteredVehRows, vehActiveIndex, pickVeh],
  );

  const handleSuccess = useCallback(() => {
    const v = formRef.current?.values ?? {};
    const polLabel =
      polRows.find((r) => String(r.ID_POL) === String(v.idPol))?.NUM_POL ??
      String(v.idPol ?? '');
    setSubmittedAssure(assureLabel);
    setSubmittedPolice(polLabel);
    setSubmittedImmat(vehLabel);
    setSubmitted(true);
    setExpanded(false);
  }, [polRows, assureLabel, vehLabel]);

  const form = useEntityForm({
    config: formConfigForForm,
    initialValues: { dateDeclaration: today },
    onSuccess: handleSuccess,
    mutationFn,
  });

  formRef.current = form;

  const idPerAssValue = form.getValue('idPerAss');
  const idPolValue = form.getValue('idPol');
  const gestionValue = form.getValue('gestion');

  useEffect(() => {
    scheduleAssureFetch(assureQuery);
  }, [assureQuery, scheduleAssureFetch]);

  // Cascade: assuré -> policies (clear police, vehicles)
  useEffect(() => {
    const id = String(idPerAssValue ?? '');
    if (!id) {
      polReqIdRef.current++;
      setPolRows([]);
      return;
    }
    void fetchPolicies(id);
  }, [idPerAssValue, fetchPolicies]);

  // Reset police/vehicle when assuré changes
  const prevAssureRef = useRef<string>('');
  useEffect(() => {
    const id = String(idPerAssValue ?? '');
    if (id !== prevAssureRef.current) {
      prevAssureRef.current = id;
      if (form.getValue('idPol')) form.setValue('idPol', '');
      if (form.getValue('idVeh')) form.setValue('idVeh', '');
      vehReqIdRef.current++;
      setVehRows([]);
      setVehLabel('');
      setVehQuery('');
    }
  }, [idPerAssValue, form]);

  // Cascade: police -> vehicles (clear vehicle)
  useEffect(() => {
    const id = String(idPolValue ?? '');
    if (!id) {
      vehReqIdRef.current++;
      setVehRows([]);
      return;
    }
    void fetchVehicles(id);
  }, [idPolValue, fetchVehicles]);

  const prevPolRef = useRef<string>('');
  useEffect(() => {
    const id = String(idPolValue ?? '');
    if (id !== prevPolRef.current) {
      prevPolRef.current = id;
      if (form.getValue('idVeh')) form.setValue('idVeh', '');
      setVehLabel('');
      setVehQuery('');
    }
  }, [idPolValue, form]);

  // Cascade: gestion -> gestion2
  useEffect(() => {
    const code = String(gestionValue ?? '');
    if (!code) {
      gestion2ReqIdRef.current++;
      setGestion2Rows([]);
      return;
    }
    void fetchGestion2(code);
  }, [gestionValue, fetchGestion2]);

  const prevGestionRef = useRef<string>('');
  useEffect(() => {
    const code = String(gestionValue ?? '');
    if (code !== prevGestionRef.current) {
      prevGestionRef.current = code;
      if (form.getValue('gestion2')) form.setValue('gestion2', '');
    }
  }, [gestionValue, form]);

  // Drive disabled state on dependent fields by reading current values
  const idPolDisabled = !idPerAssValue;
  const idVehDisabled = !idPolValue;
  const gestion2Disabled = !gestionValue;

  // The Assuré typeahead — a bespoke searchable control backed by `assure*`
  // state. It is not stock auto-rendered, but carries a config descriptor
  // (`assure`) so its label / placeholder / visibility are rule-targetable: the
  // control reads them off the override-merged `assureDescriptor` (never
  // hardcoded JSX strings). Rendered as the first grid cell (before every
  // config field) when visible, so it never reorders with the form fields.
  const assureControl = (
    <div key="assure" className="col-span-2 relative" ref={assureWrapperRef}>
      <TextField
        label={assureDescriptor?.label ?? 'Assuré'}
        required={assureRequired}
        placeholder={assureDescriptor?.placeholder ?? 'Rechercher un assuré...'}
        value={assureLabel || assureQuery}
        onChange={(e) => {
          if (assureLabel) clearAssureSelection();
          setAssureQuery(e.target.value);
          setAssureOpen(true);
        }}
        onFocus={() => setAssureOpen(true)}
        onKeyDown={handleAssureKeyDown}
        data-testid="assure-input"
      />
      {assureOpen && assureRows.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-secondary-200 rounded-md shadow-lg max-h-60 overflow-y-auto z-10"
          data-testid="assure-list"
        >
          <ul>
            {assureRows.map((r, idx) => (
              <li key={String(r.ID_PER)}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickAssure(r);
                  }}
                  className={[
                    'w-full flex items-center gap-3 px-3 py-2 text-left text-sm',
                    idx === assureActiveIndex
                      ? 'bg-primary-100 text-primary-900'
                      : 'hover:bg-secondary-50',
                  ].join(' ')}
                >
                  {r.NOM2_PER}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // The Immatriculation typeahead — bespoke control backed by `veh*` state,
  // rendered at the `immat` descriptor's sorted position (order 3, right after
  // `idPol`). Not stock auto-rendered, but carries a config descriptor
  // (`immat`) so its label / (enabled-state) placeholder /
  // visibility are rule-targetable: the control reads them off the
  // override-merged `immatDescriptor`. The disabled-state placeholder is a
  // runtime cascade hint ("pick a police first") and stays a literal.
  const immatControl = (
    <div key="immat" className="col-span-2 relative" ref={vehWrapperRef}>
      <TextField
        label={immatDescriptor?.label ?? 'Immatriculation'}
        required={immatRequired}
        disabled={idVehDisabled}
        placeholder={
          idVehDisabled
            ? 'Sélectionner d’abord une police'
            : (immatDescriptor?.placeholder ??
              'Rechercher une immatriculation...')
        }
        value={vehLabel || vehQuery}
        onChange={(e) => {
          if (vehLabel) clearVehSelection();
          setVehQuery(e.target.value);
          setVehOpen(true);
        }}
        onFocus={() => {
          if (!idVehDisabled) setVehOpen(true);
        }}
        onKeyDown={handleVehKeyDown}
        data-testid="immat-input"
      />
      {vehOpen && !idVehDisabled && filteredVehRows.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-secondary-200 rounded-md shadow-lg max-h-60 overflow-y-auto z-10"
          data-testid="immat-list"
        >
          <ul>
            {filteredVehRows.map((r, idx) => (
              <li key={String(r.ID_VEH)}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickVeh(r);
                  }}
                  className={[
                    'w-full flex items-center gap-3 px-3 py-2 text-left text-sm',
                    idx === vehActiveIndex
                      ? 'bg-primary-100 text-primary-900'
                      : 'hover:bg-secondary-50',
                  ].join(' ')}
                >
                  {r.IMMAT_VEH}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // Auto-render the form fields in config `order` (not JSX source order), so a
  // `reorderFields` layout rule reorders them with no JSX edit. The two bespoke
  // async typeaheads carry descriptor-only config keys (`assure` / `immat`),
  // excluded here from the stock `<EntityForm.Field>` auto-render and replaced
  // by their bespoke control at the descriptor's sorted position — so a reorder
  // rule on `assure` / `immat` moves the widget too, and a `visible: false`
  // hides it. The folded `heureSurvenance` `time` field now auto-renders like
  // any other field (order 5). An empty grid spacer follows `dateDeclaration`
  // and `risque4` (preserving the historic single-column rows). `idPol` /
  // `gestion2` keep their runtime disabled/options overrides driven by the
  // cascades.
  const fieldElements: ReactNode[] = [];
  const sortedFields = Object.entries(formConfig.fields).sort(
    (a, b) =>
      (a[1].order ?? Number.MAX_SAFE_INTEGER) -
      (b[1].order ?? Number.MAX_SAFE_INTEGER),
  );
  for (const [name] of sortedFields) {
    if (name === ASSURE_FIELD_KEY) {
      // Descriptor-only custom slot: render the bespoke Assuré typeahead in
      // place (honoring its config `order` / `visible`), not a stock field.
      if (assureVisible) fieldElements.push(assureControl);
      continue;
    }
    if (name === IMMAT_FIELD_KEY) {
      // Descriptor-only custom slot: render the bespoke Immatriculation
      // typeahead in place (honoring its config `order` / `visible`).
      if (immatVisible) fieldElements.push(immatControl);
      continue;
    }
    if (name === 'idPol') {
      fieldElements.push(
        <EntityForm.Field
          key="idPol"
          name="idPol"
          overrides={{
            disabled: idPolDisabled,
            options: {
              items: polItems,
              searchable: true,
              placeholder: idPolDisabled
                ? 'Sélectionner d’abord un assuré'
                : 'Sélectionner une police',
            } as SelectFieldOptions,
          }}
        />,
      );
    } else if (name === 'gestion2') {
      fieldElements.push(
        <EntityForm.Field
          key="gestion2"
          name="gestion2"
          overrides={{
            disabled: gestion2Disabled,
            options: {
              items: gestion2Items,
              searchable: true,
              placeholder: gestion2Disabled
                ? 'Sélectionner d’abord une gestion'
                : 'Sélectionner',
            } as SelectFieldOptions,
          }}
        />,
      );
    } else {
      fieldElements.push(<EntityForm.Field key={name} name={name} />);
    }

    if (SPACER_AFTER_FIELDS.has(name)) {
      fieldElements.push(<div key={`spacer-${name}`} />);
    }
  }

  if (submitted) {
    return (
      <Card id={actionAnchorId('claimflow.ouvertureSinistreCard')}>
        <Card.Header
          collapsible
          defaultExpanded={expanded}
          onExpandChange={setExpanded}
          action={<Icon icon={CheckCircle2} size="md" color="success" />}
        >
          <Icon icon={FilePlus2} size="md" color="primary" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Ouverture sinistre', _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            ({[submittedAssure, submittedPolice, submittedImmat]
              .filter(Boolean)
              .join(' / ')})
          </Text>
        </Card.Header>
        <Card.Content>
          <EntityForm config={formConfig} form={form}>
            {/* Read-only recap. The bespoke typeahead labels and the folded
                heure field stay config-driven so rename/hide rules carry into
                the collapsed view too. */}
            <TextField
              label={assureDescriptor?.label ?? 'Assuré'}
              value={assureLabel}
              disabled
              readOnly
            />
            <EntityForm.Field name="idPol" overrides={{ disabled: true }} />
            <TextField
              label={immatDescriptor?.label ?? 'Immatriculation'}
              value={vehLabel}
              disabled
              readOnly
            />
            <EntityForm.Field
              name="dateSurvenance"
              overrides={{ disabled: true }}
            />
            <EntityForm.Field
              name="heureSurvenance"
              overrides={{ disabled: true }}
            />
            <EntityForm.Field
              name="dateDeclaration"
              overrides={{ disabled: true }}
            />
            <div className="pt-4">
              <Button
                variant="primary"
                leftIcon={<Icon icon={FilePlus2} size="sm" />}
                disabled
                className="w-full"
              >
                Sinistre ouvert
              </Button>
            </div>
          </EntityForm>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card id={actionAnchorId('claimflow.ouvertureSinistreCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={FilePlus2} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Ouverture sinistre', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Alert />
          {/*
            Auto-rendered from `formConfig.fields` sorted by `order` (see
            `fieldElements`). Each `<EntityForm.Field>` carries its own
            `col-span-*` wrapper from `field.colSpan`, reproducing the historic
            two-column layout; the non-field controls are spliced at fixed
            anchors. A `reorderFields` rule changes the sort — and thus the
            rendered field order — with no JSX edit.
          */}
          <div className="grid grid-cols-2 gap-3">{fieldElements}</div>
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Icon icon={FilePlus2} size="sm" />}
                loading={form.isSubmitting}
                disabled={form.isSubmitting}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ?? 'Ouvrir le sinistre'}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}
